import { LitElement, html, nothing, TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { styleMap } from 'lit/directives/style-map.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import 'components/discovery/product/stock-indication/stock-indication.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { style } from './product-preview.style.js'

export type ProductPreviewStockIndicationVariant = 'minimal' | 'simple' | 'default'
export type ProductPreviewStockIndicationStyle = 'success' | 'warning' | 'error' | 'info'
export type ProductPreviewPriceVariant = 'default' | 'sale'

export const productPreviewHiddenFields = ['image'] as const

@customElement('csc-product-preview')
export class CSCProductPreview extends HiddenFieldsMixin(LitElement, productPreviewHiddenFields) {
  static override styles = [hostStyle, style]

  @property({ attribute: 'product-name' })
  productName: string = ''

  @property({ attribute: 'product-id' })
  productId: string = ''

  @property()
  price: string = ''

  @property({ attribute: 'price-lead' })
  priceLead: string = ''

  @property({ attribute: 'price-variant' })
  priceVariant: ProductPreviewPriceVariant = 'default'

  @property()
  uom: string = ''

  @property()
  stock: string = ''

  @property({ attribute: 'stock-warning-amount' })
  stockWarningAmount: string = ''

  @property({ attribute: 'stock-indication-variant' })
  stockIndicationVariant: ProductPreviewStockIndicationVariant = 'default'

  @property({ attribute: 'stock-indication-style' })
  stockIndicationStyle: ProductPreviewStockIndicationStyle | '' = ''

  @property({ attribute: 'stock-indication-icon' })
  stockIndicationIcon = ''

  @property({ attribute: 'stock-indication-text' })
  stockIndicationText = ''

  @property({ attribute: 'stock-tooltip' })
  stockTooltip = ''

  @property()
  comment: string = ''

  @property()
  size: 'small' | 'large' = 'small'

  @property({ attribute: 'image-source' })
  imageSource: string = ''

  @property({ attribute: 'fallback-image-source' })
  fallbackImageSource: string = ''

  @property({ attribute: 'page-link' })
  pageLink: string = ''

  @property({ type: Number, attribute: 'product-name-clamp' })
  productNameClamp: number | null = null

  private getImageSize(): number {
    return this.size === 'large' ? 75 : 50
  }

  override render() {
    const imageSize = this.getImageSize()
    return html`
      <div class="csc-product-preview">
        ${!this.isHidden('image') ? html`
          <cse-image
            source="${this.imageSource || this.fallbackImageSource}"
            alt="Product Preview"
            width="${imageSize}"
            height="${imageSize}"
            fallback-source="${this.fallbackImageSource || nothing}"
          ></cse-image>
        ` : nothing}
        <cse-list>
          ${this.renderListItem(this.productName, this.renderProductName)}
          ${this.renderListItem(this.productId, this.renderProductId)}
          ${this.renderListItem(this.price, this.renderPrice)}
          ${this.shouldRenderStock() ? this.renderListItem(true, this.renderStock) : nothing}
          ${this.renderListItem(this.comment, this.renderComment)}
        </cse-list>
      </div>
    `
  }

  private shouldRenderStock(): boolean {
    return this.stock !== '' || Boolean(this.stockIndicationStyle)
  }

  private renderListItem = (condition: any, content: () => TemplateResult) => {
    return condition
      ? html`<cse-list-item>${content()}</cse-list-item>`
      : nothing
  }

  private renderProductName = () => {
    if (this.pageLink) {
      return html`
        <cse-link
          url="${this.pageLink}"
          variant="secondary"
          font-variant="product-title-m"
        >
          <span
            class="csc-product-preview__title${this.productNameClamp != null ? ' csc-product-preview__title--clamped' : ''}"
            style=${this.productNameClamp != null ? styleMap({ '--csc-product-preview-title-line-clamp': String(this.productNameClamp) }) : nothing}
          >
            ${this.productName}
          </span>
        </cse-link>
      `
    }

    return html`<cse-text variant="product-title-m" .clamp=${this.productNameClamp}>${this.productName}</cse-text>`
  }

  private renderProductId = () =>
    html`<cse-text .clamp=${2}>${this.productId}</cse-text>`

  private renderPrice = () => {
    return html`<cse-price
      size="s"
      variant="${this.priceVariant}"
      lead="${ifDefined(this.priceLead || undefined)}"
      uom="${ifDefined(this.uom || undefined)}"
    >${this.price}</cse-price>`
  }

  private renderStock = () =>
    html`<csc-stock-indication
      stock="${this.stock}"
      warning-amount="${this.stockWarningAmount || nothing}"
      variant="${this.stockIndicationVariant}"
      indication-style="${ifDefined(this.stockIndicationStyle || undefined)}"
      indication-icon="${ifDefined(this.stockIndicationIcon || undefined)}"
      indication-text="${ifDefined(this.stockIndicationText || undefined)}"
      tooltip-text="${ifDefined(this.stockTooltip || undefined)}"
    ></csc-stock-indication>`

  private renderComment = () =>
    html`<cse-text has-icon .clamp=${2}><cse-icon slot="prefix" icon-name="comment" icon-style="regular" size="sm"></cse-icon>${this.comment}</cse-text>`
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-product-preview': CSCProductPreview
  }
}
